import { FC, useMemo, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Form, Formik, FormikHelpers } from "formik";
import { useTheme } from "@mui/material";
import {
  Topbar,
  types,
  toast,
  PageLoader,
  shared
} from "@vilocnv/allsetra-core";
import { capitalize, isEmpty } from "lodash";
import AccountSettingsSection from "components/sections/AccountSettingsSection/AccountSettingsSection";

// Data
import { useAppDispatch, useAppSelector, useCurrentUser } from "hooks";
import {
  selectActiveAccountState,
  selectDashboardCountriesState
} from "app/data/selectors";
import {
  updateAccountThunk,
  getSpecificAccountThunk,
  resetActiveAccountState
} from "app/features";
import {
  VAT_MESSAGE_KEYS,
  accountSettingsFormatterForForm,
  accountSettingsValidationSchema,
  findCountryAlpha2,
  formatDataforPayload,
  isBusinessCustomer,
  isEuVatCountry,
  isVatBlockingSave,
  signalRGenerateSuccessToastMessage
} from "app/data/helpers";
import { useTranslation } from "react-i18next";
import { SignalRService } from "app/data/services";

// A save that changes the VAT number waits for VIES in the worker, which took
// ~25 s on a cold call.
const SAVE_CONFIRMATION_TIMEOUT_MS = 30_000;

const AccountDetails: FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const params = useParams();
  const { isDriver } = useCurrentUser();

  const [formSubmitting, setFormSubmitting] = useState(false);

  // Global State
  const {
    specificAccount,
    activeAccountId,
    loading: isFetching
  } = useAppSelector(selectActiveAccountState);
  const { countries } = useAppSelector(selectDashboardCountriesState);
  const safeCountries = Array.isArray(countries) ? countries : [];

  const { t } = useTranslation(["translation", "formFieldsTranslation"]);

  const getSpecificAccountById = async () => {
    try {
      const { type } = await dispatch(getSpecificAccountThunk(params.id ?? ""));

      if (type !== "accounts/getSpecificAccountThunk/fulfilled") {
        navigate("/dashboard/accounts");
        toast.error(t("errors.accounts.notFound"));
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (isDriver) {
      navigate("/dashboard", { replace: true });
      return;
    }

    if (isEmpty(params.id)) {
      dispatch(resetActiveAccountState());
      navigate("/dashboard/accounts");
    } else {
      getSpecificAccountById();
    }
  }, [params.id, navigate, dispatch]);

  const initialValues = useMemo<Record<string, any>>(
    () =>
      !isEmpty(specificAccount)
        ? accountSettingsFormatterForForm(specificAccount)
        : {},
    [specificAccount, activeAccountId]
  );

  const isBusiness = useMemo(
    () => isBusinessCustomer(specificAccount ?? {}),
    [specificAccount]
  );

  const validationSchema = accountSettingsValidationSchema(t, {
    isBusinessCustomer: isBusiness,
    countries: safeCountries,
    initialVatNumber: initialValues.vatNumber,
    initialCountryId: initialValues.billingAddress?.countryId
  });

  // `status.vat` is published by the VAT field (useVatValidation).
  const isVatBlocking = (values: any, status: any) => {
    const countryCode = findCountryAlpha2(
      safeCountries,
      values.billingAddress?.countryId
    );

    return isVatBlockingSave(status?.vat, {
      required: isBusiness && isEuVatCountry(countryCode),
      value: values.vatNumber,
      countryCode
    });
  };

  const onFulfilledRequest = (
    accountName: string,
    formikHelpers: FormikHelpers<types.IAccount>
  ) => {
    getSpecificAccountById();
    formikHelpers.setSubmitting(false);
    setFormSubmitting(false);
    toast.success(
      signalRGenerateSuccessToastMessage(accountName, "Account", "updated")
    );
  };

  const saveChangesHandler = async (
    values: types.IAccount,
    formikHelpers: FormikHelpers<types.IAccount>
  ) => {
    formikHelpers.setSubmitting(true);
    setFormSubmitting(true);

    const payload = formatDataforPayload(values);

    const { type } = await dispatch(
      updateAccountThunk({ accountId: values?.uniqueId, data: payload })
    );

    if (type === "accounts/updateAccountThunk/fulfilled") {
      // The update is accepted (202) before the worker validates it, and a
      // rejected VAT emits no event: never report success without the event.
      const event = await shared
        .waitSinalREvent({
          hubConnection: SignalRService.hubConnection,
          filterFn: (event: any) =>
            event?.eventName === types.BackendEventsEnum.AccountUpdatedEvent &&
            event?.uniqueId === values.uniqueId,
          timeoutMs: SAVE_CONFIRMATION_TIMEOUT_MS
        })
        .catch(() => null);

      if (event) {
        onFulfilledRequest(event.name, formikHelpers);
      } else {
        formikHelpers.setSubmitting(false);
        setFormSubmitting(false);
        toast.warning(
          t(VAT_MESSAGE_KEYS.saveNotConfirmed, { ns: "formFieldsTranslation" })
        );
      }
    } else {
      formikHelpers.setSubmitting(false);
      setFormSubmitting(false);
    }
  };

  return (
    <main>
      {isFetching ? (
        <PageLoader />
      ) : (
        <Formik
          // @ts-ignore
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={saveChangesHandler}
          enableReinitialize
          validateOnMount
        >
          {({
            handleSubmit,
            isSubmitting,
            isValid,
            dirty,
            resetForm,
            values,
            status
          }) => (
            <Form>
              <Topbar
                theme={theme}
                title="Account"
                breadcrumbTitle={capitalize(specificAccount?.name) ?? "Go Back"}
                breadcrumbRedirectTo={() => navigate(-1)}
                primaryButton={{
                  variant: "contained",
                  text: t("buttonsText.saveChanges"),
                  onClick: handleSubmit,
                  loading: isSubmitting || formSubmitting,
                  disabled:
                    (!dirty ? isValid : !isValid) ||
                    isVatBlocking(values, status)
                }}
                secondaryButton={{
                  variant: "text",
                  text: t("buttonsText.cancel"),
                  onClick: () => resetForm()
                }}
              />
              <AccountSettingsSection />
            </Form>
          )}
        </Formik>
      )}
    </main>
  );
};

export default AccountDetails;
